import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase/config.js';

export default function AuthAction() {
  const [params] = useSearchParams();
  const mode = params.get('mode');
  const oobCode = params.get('oobCode');

  if (!mode || !oobCode) {
    return <Card><p>Missing action parameters.</p><Back /></Card>;
  }

  if (mode === 'resetPassword') return <ResetPassword oobCode={oobCode} />;
  if (mode === 'verifyEmail') return <VerifyEmail oobCode={oobCode} />;
  if (mode === 'recoverEmail') return <RecoverEmail oobCode={oobCode} />;

  return <Card><p>Unknown action: {mode}</p><Back /></Card>;
}

function ResetPassword({ oobCode }) {
  const [stage, setStage] = useState('verifying');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    verifyPasswordResetCode(auth, oobCode)
      .then((mail) => { setEmail(mail); setStage('ready'); })
      .catch((e) => { setErr(e.message.replace('Firebase: ', '')); setStage('error'); });
  }, [oobCode]);

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (pw.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (pw !== pw2) { setErr('Passwords do not match'); return; }
    setBusy(true);
    try {
      await confirmPasswordReset(auth, oobCode, pw);
      setStage('done');
    } catch (e) {
      setErr(e.message.replace('Firebase: ', ''));
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'verifying') return <Card><p>Verifying link…</p></Card>;
  if (stage === 'error') return <Card><p style={{ color: 'var(--danger)' }}>{err}</p><Back /></Card>;
  if (stage === 'done') {
    return (
      <Card>
        <h2 style={{ marginTop: 0 }}>Password set.</h2>
        <p style={{ color: 'var(--text-dim)' }}>You can now sign in with your new password.</p>
        <button className="primary" onClick={() => nav('/login')} style={{ width: '100%' }}>Sign in</button>
      </Card>
    );
  }

  return (
    <Card>
      <h2 style={{ marginTop: 0 }}>Set a new password</h2>
      <p style={{ color: 'var(--text-dim)', fontSize: 14 }}>For <b>{email}</b></p>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input type="password" placeholder="new password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus required />
        <input type="password" placeholder="confirm password" value={pw2} onChange={(e) => setPw2(e.target.value)} required />
        {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
        <button className="primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save password'}</button>
      </form>
    </Card>
  );
}

function VerifyEmail({ oobCode }) {
  const [stage, setStage] = useState('verifying');
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    applyActionCode(auth, oobCode)
      .then(() => setStage('done'))
      .catch((e) => { setErr(e.message.replace('Firebase: ', '')); setStage('error'); });
  }, [oobCode]);

  if (stage === 'verifying') return <Card><p>Verifying email…</p></Card>;
  if (stage === 'error') return <Card><p style={{ color: 'var(--danger)' }}>{err}</p><Back /></Card>;
  return (
    <Card>
      <h2 style={{ marginTop: 0 }}>Email verified.</h2>
      <button className="primary" onClick={() => nav('/login')} style={{ width: '100%' }}>Sign in</button>
    </Card>
  );
}

function RecoverEmail({ oobCode }) {
  const [stage, setStage] = useState('applying');
  const [err, setErr] = useState(null);

  useEffect(() => {
    applyActionCode(auth, oobCode)
      .then(() => setStage('done'))
      .catch((e) => { setErr(e.message.replace('Firebase: ', '')); setStage('error'); });
  }, [oobCode]);

  if (stage === 'applying') return <Card><p>Restoring your email…</p></Card>;
  if (stage === 'error') return <Card><p style={{ color: 'var(--danger)' }}>{err}</p><Back /></Card>;
  return <Card><h2 style={{ marginTop: 0 }}>Email restored.</h2><Back /></Card>;
}

function Card({ children }) {
  return (
    <div className="centered" style={{ flexDirection: 'column', padding: 24 }}>
      <img src="/trainforge-logo.svg" alt="" width={64} height={64} style={{ marginBottom: 16 }} />
      <div className="wordmark" style={{ fontSize: '1.4rem', marginBottom: 24 }}>GEARBOX</div>
      <div style={{ width: '100%', maxWidth: 380, background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
        {children}
      </div>
    </div>
  );
}

function Back() {
  return <Link to="/login" style={{ color: 'var(--accent)' }}>Back to sign in</Link>;
}
